import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import emptyState from '../../../static/images/emptyState.png';
import useWindowSize from '../../../hooks/windowSize';
import { useLocation } from 'react-router-dom';
import { PagesRoutes } from '../../../routing/PagesRoutes';

const minDrawerWidth = 392;
const maxDrawerWidth = 710;

export type DrawerTriggerProps = {
    onClick: () => void;
};
export type desktopVariantType = 'permanent' | 'persistent' | 'temporary';
type Props = {
    isOpen: boolean;
    setIsOpen: () => void;
    drawerContent: JSX.Element;
    isSideBarOpen: boolean;
    setSideBarOpen: (open: boolean) => void;
    isEmptyState?: boolean;
    printMode?: boolean;
    hasChat?: boolean;
    desktopVariant?: desktopVariantType;
    defaultDrawerWidth?: number;
    drawerType?: string | null;
};

function ResponsiveDrawer({
    isOpen,
    setIsOpen,
    drawerContent,
    isSideBarOpen,
    setSideBarOpen,
    isEmptyState,
    printMode,
    children,
    hasChat,
    desktopVariant = 'persistent',
    defaultDrawerWidth = 0,
    drawerType
}: PropsWithChildren<Props>) {
    const [windowWidth] = useWindowSize();
    const [drawerWidth, setDrawerWidth] = useState(defaultDrawerWidth);
    const location = useLocation();
    const [animatedContent, setAnimatedContent] = useState(true);

    const pageOffsetRight = hasChat && !printMode && !location.pathname.includes(PagesRoutes.Messages) && windowWidth >= 768 ? 70 : 0;
    
    // Calculate padding based on drawer type and chat presence
    const getDrawerPadding = () => {
        if (drawerType === 'ADD_EDIT_USER') {
            return '70px';
        }
        if ((drawerType === 'CHAT' || drawerType === 'CHAT_REQUESTS') && pageOffsetRight) {
            return windowWidth >= 768 ? '70px' : '80px';
        }
        return '10px';
    };

    // Styled components
    const RootDiv = styled('div')<{ pageOffsetRight: number }>(({ theme, pageOffsetRight }) => ({
        [theme.breakpoints.up('sm')]: {
            display: 'flex'
        },
        zIndex: 0,
        paddingRight: pageOffsetRight,
        height: '100%'
    }));

    const Dragger = styled('div')(() => ({
        width: '5px',
        cursor: 'ew-resize',
        padding: '4px 0 0',
        borderTop: '1px solid #ddd',
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 100,
        backgroundColor: 'transparent',
        userSelect: 'none'
    }));

    const Toolbar = styled('div')<{ printMode?: boolean }>(({ printMode }) => ({
        height: printMode ? 0 : '64px'
    }));

    const CloseMenuContainer = styled('div')(() => ({
        position: 'absolute',
        backgroundColor: 'transparent',
        top: 10,
        right: 10,
        zIndex: 1005
    }));

    const CloseMenuButton = styled(IconButton)(() => ({
        marginRight: 'auto',
        marginLeft: 0,
        backgroundColor: '#f5f5f5',
        '&:hover': {
            backgroundColor: '#e0e0e0'
        }
    }));


    const drawerPaperSx = {
        width: defaultDrawerWidth,
        zIndex: 1003,
        top: '64px !important',
        marginTop: '0px !important',
        right: pageOffsetRight,
        height: 'calc(100vh - 64px) !important',
        position: 'fixed !important',
        '@media (max-width: 600px)': {
            width: '100%'
        }
    };

    const drawerModalSx = {
        zIndex: '1003!important'
    };

    useEffect(() => {
        setAnimatedContent(location.pathname.indexOf('/shipments/') === -1);
        if (windowWidth < 1280 && isOpen && isSideBarOpen) {
            setSideBarOpen(false);
        }
        setDrawerWidth(defaultDrawerWidth);
    }, [isOpen, windowWidth, setSideBarOpen, isSideBarOpen, location, defaultDrawerWidth]);

    const handleMouseDown = () => {
        document.addEventListener('mouseup', handleMouseUp, true);
        document.addEventListener('mousemove', handleMouseMove, true);
    };

    const handleMouseUp = () => {
        document.removeEventListener('mouseup', handleMouseUp, true);
        document.removeEventListener('mousemove', handleMouseMove, true);
    };

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            const newWidth = window.innerWidth - e.clientX;

            if (newWidth > minDrawerWidth && newWidth < maxDrawerWidth) {
                setDrawerWidth(newWidth - pageOffsetRight);
            }
        },
        [pageOffsetRight]
    );
    function handleDrawerToggle() {
        setIsOpen();
    }
    const drawer = (
        <div style={{ height: '100%', backgroundColor: '#ffffff', position: 'relative' }}>
            <CloseMenuContainer>
                <CloseMenuButton onClick={handleDrawerToggle}>
                    <CloseIcon />
                </CloseMenuButton>
            </CloseMenuContainer>
            <Dragger onMouseDown={handleMouseDown} />
            <div style={{ height: 'calc(100% - 50px)', marginTop: '50px', backgroundColor: '#ffffff', overflow: 'auto' }}>{drawerContent}</div>
            {isEmptyState && <img src={emptyState} alt="" style={{ position: 'absolute', right: -48, bottom: -74 }} height="250px" />}
        </div>
    );
    const notTemporary = windowWidth >= 1280 && desktopVariant !== 'temporary';
    
    return (
        <RootDiv pageOffsetRight={pageOffsetRight}>
            <CssBaseline />

            <nav>
                {notTemporary ? (
                    <Drawer
                        variant={desktopVariant}
                        anchor="right"
                        open={isOpen}
                        onClose={handleDrawerToggle}
                        slots={{ backdrop: () => null }}
                        sx={drawerPaperSx}
                        ModalProps={{
                            keepMounted: true // Better open performance on mobile.
                        }}
                        PaperProps={{ 
                            style: { 
                                width: drawerWidth, 
                                overflow: isEmptyState ? 'hidden' : 'auto',
                                top: '64px',
                                height: 'calc(100vh - 64px)',
                                position: 'fixed',
                                zIndex: 1003,
                                paddingRight: getDrawerPadding(),
                                backgroundColor: '#ffffff'
                            } 
                        }}
                    >
                        {drawer}
                    </Drawer>
                ) : (
                    <Drawer
                        variant="temporary"
                        disableEnforceFocus
                        anchor="right"
                        onClose={handleDrawerToggle}
                        open={isOpen}
                        sx={{
                            width: defaultDrawerWidth,
                            flexShrink: 0,
                            '& .MuiPaper-root': drawerPaperSx,
                            '& .MuiModal-root': drawerModalSx
                        }}
                        slots={{ backdrop: () => null }}
                        PaperProps={{ 
                            style: { 
                                width: windowWidth >= 768 ? drawerWidth : '100%', 
                                overflow: isEmptyState ? 'hidden' : 'auto',
                                top: '64px',
                                height: 'calc(100vh - 64px)',
                                position: 'fixed',
                                zIndex: 1003,
                                paddingRight: getDrawerPadding(),
                                backgroundColor: '#ffffff'
                            } 
                        }}
                    >
                        {drawer}
                    </Drawer>
                )}
            </nav>
            <Box
                style={{
                    width: notTemporary ? `${windowWidth - drawerWidth - pageOffsetRight}px` : `${windowWidth - pageOffsetRight}px`,
                    marginRight: isOpen && notTemporary ? `${drawerWidth}px` : '0px',
                    height: '100%'
                }}
                sx={{
                    flexGrow: 1,
                    ...(animatedContent
                        ? {
                              '@media (min-width: 1280px)': {
                                  transition: isOpen
                                      ? 'margin 225ms cubic-bezier(0.0, 0, 0.2, 1) 0ms'
                                      : 'margin 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
                                  marginRight: isOpen ? defaultDrawerWidth + pageOffsetRight : pageOffsetRight
                              }
                          }
                        : {
                              '@media (min-width: 1280px)': {
                                  marginRight: isOpen ? defaultDrawerWidth + pageOffsetRight : pageOffsetRight
                              }
                          })
                }}
            >
                <Toolbar printMode={printMode} />
                {children}
            </Box>
        </RootDiv>
    );
}

export default ResponsiveDrawer;
